/**
 * Invoice API Service
 * Handles all invoice-related API calls
 */

import { apiClient } from '../client';
import type { Invoice, InvoiceCreate, InvoiceListResponse } from '../../types/api';

export const invoicesApi = {
  /**
   * Get all invoices with optional filters
   */
  getAll: async (params?: {
    skip?: number;
    limit?: number;
    search?: string;
    status?: string;
    customer_id?: number;
  }): Promise<InvoiceListResponse> => {
    const response = await apiClient.get<InvoiceListResponse>('/invoices/', { params });
    return response.data;
  },

  /**
   * Get invoice by ID
   */
  getById: async (id: number): Promise<Invoice> => {
    const response = await apiClient.get<Invoice>(`/invoices/${id}`);
    return response.data;
  },

  /**
   * Create new invoice
   */
  create: async (data: InvoiceCreate): Promise<Invoice> => {
    const response = await apiClient.post<Invoice>('/invoices/', data);
    return response.data;
  },

  /**
   * Update existing invoice
   */
  update: async (id: number, data: Partial<InvoiceCreate>): Promise<Invoice> => {
    const response = await apiClient.put<Invoice>(`/invoices/${id}`, data);
    return response.data;
  },

  /**
   * Delete invoice
   */
  delete: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/invoices/${id}`);
    return response.data;
  },

  /**
   * Send invoice to customer
   */
  send: async (id: number): Promise<{ success: boolean; message: string; sent_date: string }> => {
    const response = await apiClient.post(`/invoices/${id}/send`);
    return response.data;
  },

  /**
   * Mark invoice as paid
   */
  markPaid: async (id: number, paymentAmount?: number): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post(`/invoices/${id}/mark-paid`, {
      payment_amount: paymentAmount,
    });
    return response.data;
  },
};

export default invoicesApi;
